import { useEffect, useMemo, useState } from "react";

import { getCoursePricesApiV1PaymentsCoursePricesGet } from "@/src/api/sdk.gen";

/** A price as the catalog writes it: "$7" rather than "$7.00", keeping cents only when there are some. */
export function formatCatalogPrice(amount: string | number, currency: string): string {
  const value = Number(amount);
  const whole = Number.isInteger(value);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(value);
  } catch {
    return `${currency.toUpperCase()} ${whole ? value : value.toFixed(2)}`;
  }
}

export type CatalogCoursePrice = {
  /** Each level on sale, lowest first, already formatted. */
  levels: Array<{ levelNumber: number; levelName: string | null; priceText: string }>;
  /** "$7", "$7 – $10" when levels differ, or "From $7" when they are priced in different currencies. */
  perLevelText: string;
  /** Every level together, or null when there is nothing to add up in one currency. */
  totalText: string | null;
};

/**
 * What catalog courses cost, from the plans checkout charges.
 *
 * The catalog used to hardcode $25 a level while those plans charged $7, so a visitor was quoted
 * one price and charged another. Returns null until the prices arrive; a course missing from the
 * result has nothing on sale.
 */
export function useCoursePrices(courseIds: Array<number | undefined | null>): Record<number, CatalogCoursePrice> | null {
  const ids = useMemo(
    () => [...new Set(courseIds.filter((id): id is number => typeof id === "number"))].sort((a, b) => a - b),
    [courseIds.join(",")],
  );
  const [prices, setPrices] = useState<Record<number, CatalogCoursePrice> | null>(null);

  useEffect(() => {
    if (ids.length === 0) {
      setPrices({});
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await getCoursePricesApiV1PaymentsCoursePricesGet({ query: { course_id: ids } });
        if (cancelled || !data) return;
        const byId: Record<number, CatalogCoursePrice> = {};
        for (const course of data.courses) {
          if (course.levels.length === 0) continue;
          const levels = course.levels.map((lvl) => ({
            levelNumber: lvl.level_number,
            levelName: lvl.level_name ?? null,
            priceText: formatCatalogPrice(lvl.price, lvl.currency),
          }));
          const numbers = course.levels.map((lvl) => Number(lvl.price));
          const oneCurrency = new Set(course.levels.map((lvl) => lvl.currency)).size === 1;
          const min = Math.min(...numbers);
          const max = Math.max(...numbers);
          const currency = course.levels[0].currency;
          byId[course.course_id] = {
            levels,
            // Levels in different currencies cannot be ranged or added up, so quote the entry level.
            perLevelText: !oneCurrency
              ? `From ${levels[0].priceText}`
              : min === max
                ? formatCatalogPrice(min, currency)
                : `${formatCatalogPrice(min, currency)} – ${formatCatalogPrice(max, currency)}`,
            totalText:
              course.total != null && course.total_currency
                ? formatCatalogPrice(course.total, course.total_currency)
                : null,
          };
        }
        setPrices(byId);
      } catch {
        // Leaves prices unshown rather than falling back to a figure checkout would not charge.
        if (!cancelled) setPrices({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ids]);

  return prices;
}
